import { FC, Fragment, useMemo, useState } from "react";
import { CircularProgress, Stack } from "@mui/material";
import {
  CellMouseOutEvent,
  CellMouseOverEvent,
  GridApi,
} from "ag-grid-community";
import { useHighchartsContext } from "@/storybook/c8r-highcharts/common/components/HighchartsProvider";
import { useAppSelector } from "@/store/hooks";
import { resourceExplorerDataGridViewSelector } from "@/store/resource-explorer/selectors/current-resource-explorer/resourceExplorerDataGridViewSelector";
import { resourceExplorerWidgetDataLoadingSelector } from "@/store/resource-explorer/selectors/loading/resourceExplorerWidgetDataLoadingSelector";
import { resourceExplorerGranularDataLoadingSelector } from "@/store/resource-explorer/selectors/loading/resourceExplorerGranularDataLoadingSelector";
import { billingExplorerChartKeyGenerator } from "@/components/pages/common/billing-explorer/utils/billingExplorerChartKeyGenerator";
import { chartSeriesHighlightFunctions } from "@/storybook/c8r-highcharts/common/utils/helpers/chartSeriesHighlightFunctions";
import { ResourceExplorerTotalViewFooter } from "@/components/pages/resource-explorer/components/resource-explorer-card/components/billing-explorer-data-grid/components/footer/ResourceExplorerTotalViewFooter";
import { ResourceExplorerGranularViewFooter } from "@/components/pages/resource-explorer/components/resource-explorer-card/components/billing-explorer-data-grid/components/footer/ResourceExplorerGranularViewFooter";
import { BillingExplorerTotalViewGrid } from "@/components/pages/resource-explorer/components/resource-explorer-card/components/billing-explorer-data-grid/components/BillingExplorerTotalViewGrid";
import { BillingExplorerGranularViewGrid } from "@/components/pages/resource-explorer/components/resource-explorer-card/components/billing-explorer-data-grid/components/BillingExplorerGranularViewGrid";
import { BillingExplorerGirdToolbar } from "@/components/pages/resource-explorer/components/resource-explorer-card/components/billing-explorer-data-grid/components/toolbar/BillingExplorerGirdToolbar";
import { resourceExplorerChartDataOthersKey } from "@/components/pages/resource-explorer/components/resource-explorer-card/utils/constants/common";
import { billingExplorerChartTypeSelector } from "@/store/resource-explorer/selectors/current-resource-explorer/billingExplorerChartTypeSelector";
import { chartPieHighlightFunctions } from "@/storybook/c8r-highcharts/pie-chart/utils/helpers/chartPieHighlightFunctions";

export const BillingExplorerGrid: FC = () => {
  const { chartApi } = useHighchartsContext();
  const chartType = useAppSelector(billingExplorerChartTypeSelector);

  const [gridApi, setGridApi] = useState<GridApi | null>(null);

  const dataGridView = useAppSelector(resourceExplorerDataGridViewSelector);
  const totalViewLoading = useAppSelector(
    resourceExplorerWidgetDataLoadingSelector,
  );
  const granularViewLoading = useAppSelector(
    resourceExplorerGranularDataLoadingSelector,
  );
  const loading = totalViewLoading || granularViewLoading;

  const rowHoverProps = useMemo(() => {
    return {
      onCellMouseOver: ({ data }: CellMouseOverEvent | CellMouseOutEvent) => {
        const key = billingExplorerChartKeyGenerator(data.field, data.label);

        if (chartType !== "pie") {
          const keyInChart = chartApi?.series?.find((s) => s?.name === key)
            ? key
            : resourceExplorerChartDataOthersKey;

          chartSeriesHighlightFunctions.onHoverIn(chartApi, keyInChart);
        } else {
          const name = chartApi?.series
            ?.at(0)
            ?.data?.find((s) => s?.name === key)
            ? key
            : resourceExplorerChartDataOthersKey;

          chartPieHighlightFunctions.onHoverIn(chartApi, name);
        }
      },

      onCellMouseOut: () => {
        if (chartType !== "pie") {
          chartSeriesHighlightFunctions.onHoverOut(chartApi);
        } else {
          chartPieHighlightFunctions.onHoverOut(chartApi);
        }
      },
    };
  }, [chartApi, chartType]);

  return (
    <Stack position="relative" height="100%">
      {loading && loadingComponent}

      <BillingExplorerGirdToolbar
        key={dataGridView}
        dataGridView={dataGridView}
        gridApi={gridApi}
      />

      {dataGridView === "total" && (
        <Fragment>
          <BillingExplorerTotalViewGrid
            setGridApi={setGridApi}
            rowHoverProps={rowHoverProps}
          />
          <ResourceExplorerTotalViewFooter />
        </Fragment>
      )}

      {dataGridView === "granular" && (
        <Fragment>
          <BillingExplorerGranularViewGrid
            setGridApi={setGridApi}
            rowHoverProps={rowHoverProps}
          />
          <ResourceExplorerGranularViewFooter />
        </Fragment>
      )}
    </Stack>
  );
};

const loadingComponent = (
  <Stack
    width="100%"
    height="100%"
    zIndex="appBar"
    fontWeight="bold"
    position="absolute"
    alignItems="center"
    justifyContent="center"
    bgcolor="rgba(255, 255, 255, 0.5)"
  >
    <CircularProgress size={24} color="info" />
  </Stack>
);
